import Bottombar from "@/components/bottombar"
import Button from "@/components/button"
import Topbar from "@/components/topbar"
import Combobox from "@/components/ui/combobox"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Icon } from "@/icons/icon"
import { posthog } from "@/lib/posthog"
import { useDisableBack } from "@/lib/utils"
import LogoutConfirmSheet from "@/components/logout-confirm-sheet"
import { deptSchemaToFormSchema, FormSchema } from "@/packaging/lib/form"
import { v2DeptSchemaToFormSchema } from "@/packaging-v2/lib/form"
import { handleNetworkError } from "@/services/base"
import {
  discardPackagingShift,
  getPrevHistory,
  startPackagingShift,
} from "@/services/packaging"
import {
  discardV2Shift,
  startV2Shift,
} from "@/services/packaging-v2"
import useStore from "@/store"
import { HTTPError } from "ky"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router"
import { toast } from "sonner"

export default function SelectDeptsPage() {
  const navigate = useNavigate()
  const packagingStore = useStore.use.packaging()
  const packagingV2Store = useStore.use.packagingV2()
  const { plant } = useStore((state) => state.user)
  const [selected, setSelected] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [showLogoutSheet, setShowLogoutSheet] = useState(false)

  useDisableBack()
  useInit()

  // Determine if this is V2 based on which process has a processId set
  const isV2 = !!packagingV2Store.shift.processId

  // Get the appropriate departments list based on the version
  const depts = isV2
    ? packagingV2Store.allDepartmentsOptions
    : packagingStore.allDepartments

  function useInit() {
    useEffect(() => {}, [])
  }

  async function handleStartLog() {
    try {
      setIsLoading(true)

      if (isV2) {
        await handleStartV2Log()
      } else {
        await handleStartV1Log()
      }
    } catch (err) {
      posthog.captureException(err)

      const handled = await handleNetworkError(err, navigate)
      if (!handled && err instanceof HTTPError) {
        toast.error(err.message)
        return
      }

      console.error(err)
      toast.error("Error adding department")
    } finally {
      setIsLoading(false)
    }
  }

  async function handleStartV1Log() {
    const forms: FormSchema[] = []
    for (const dept of selected) {
      const schema = packagingStore.getDeptSchema(dept)
      if (!schema) {
        return
      }

      const form = deptSchemaToFormSchema(schema)
      forms.push(form)
      void getPrevHistory(dept)
    }

    // Call API first before updating local state
    await startPackagingShift({
      shiftId: packagingStore.shift.shiftId,
      processId: packagingStore.shift.processId,
    })

    // Only update local state and navigate after API call succeeds
    packagingStore.addDeptForms(forms)
    await navigate("/packaging/shift")
  }

  async function handleStartV2Log() {
    const forms = []
    for (const deptId of selected) {
      const schema = packagingV2Store.getDeptSchema(deptId)
      if (!schema) {
        continue
      }

      const form = v2DeptSchemaToFormSchema(schema)
      forms.push(form)
    }

    // Call API first before updating local state
    await startV2Shift({
      shiftId: packagingV2Store.shift.shiftId,
      processId: packagingV2Store.shift.processId,
    })

    // Only update local state and navigate after API call succeeds
    packagingV2Store.addDeptForms(forms)
    await navigate("/packaging-v2")
  }

  function handleSelectDepts(value: string[]) {
    setSelected(value)
  }

  function isValid() {
    return !!selected.length
  }

  async function handleClickDiscard() {
    try {
      if (isV2) {
        await discardV2Shift()
        packagingV2Store.clearShift()
      } else {
        await discardPackagingShift()
        packagingStore.clearShift()
      }
      await navigate("/manage-processes")
    } catch (err) {
      const handled = await handleNetworkError(err, navigate)

      if (!handled && err instanceof HTTPError) {
        toast.error(err.message)
        return
      }

      console.error(err)
      toast.error("Error discarding shift")
    }
  }

  const pageTitle = isV2 ? "Packaging V2" : "Packaging"

  return (
    <div className="relative h-dvh">
      <Topbar className="sticky" title="Unit Quality" subtitle={plant.name}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline">
              <Icon name="MoreVertical" className="text-button-black h-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent inPortal>
            <DropdownMenuItem onClick={handleClickDiscard}>
              Discard Process
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setShowLogoutSheet(true)}>
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </Topbar>

      <div className="p-4 pt-8">
        <h2 className="text-button-black text-2xl font-semibold">{pageTitle}</h2>

        <Combobox
          className="mt-6"
          label="Select Departments (Multi select)"
          placeholder="Select Departments"
          options={depts}
          maxAllowed={4}
          multiple
          onChange={handleSelectDepts}
        />
      </div>

      <Bottombar className="absolute bottom-0 flex gap-4">
        <Button
          className="grow"
          disabled={!isValid()}
          isLoading={isLoading}
          onClick={handleStartLog}
        >
          Start Log
        </Button>
      </Bottombar>

      <LogoutConfirmSheet
        open={showLogoutSheet}
        onOpenChange={setShowLogoutSheet}
      />
    </div>
  )
}
